import React from 'react';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginStart, loginSuccess, loginFailure } from '../../redux/userSlice';
import api from '../../lib/api';
import './Login.css';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { loading, error } = useSelector((state) => state.user);
    
    // Get the page user was trying to access before login
    const from = location.state?.from || null;

    const onFinish = async (values) => {
        try {
            dispatch(loginStart());
            const response = await api.post('/login', values);
            
            if (response.data && response.data.user) {
                dispatch(loginSuccess(response.data));
                message.success('Login successful!');
                
                // Temp accounts must change password before anything else
                if (response.data.user.must_change_password) {
                    navigate('/change-password-required', { replace: true });
                    return;
                }

                // Check approval status first
                if (response.data.user.approval_status !== 'approved' && !response.data.user.roles?.includes('admin')) {
                    // Redirect to pending approval screen
                    navigate('/pending-approval');
                    return;
                }
                
                // Redirect to the page user was trying to access, or default dashboard
                if (from) {
                    navigate(from, { replace: true });
                } else if (response.data.user.roles && Array.isArray(response.data.user.roles)) {
                    const isAdmin = response.data.user.roles.includes('admin');
                    navigate(isAdmin ? '/admin/dashboard' : '/dashboard', { replace: true });
                } else {
                    console.error('User roles not found in response:', response.data.user);
                    message.error('User role information is missing');
                }
            } else {
                console.error('Invalid response structure:', response.data);
                dispatch(loginFailure());
                message.error('Invalid response from server');
            }
        } catch (error) {
            console.error('Login error details:', error.response?.data);
            dispatch(loginFailure());
            message.error(
                error.response?.data?.message || 
                error.message || 
                'Login failed. Please check your credentials.'
            );
        }
    };

    return (
        <div className="login-container">
            <div className="login-content">
                {/* MaveshiBids Logo */}
                <div className="logo-section" style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <img 
                        src="/assets/images/logo.png" 
                        alt="MaveshiBids" 
                        style={{ 
                            height: '60px', 
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease'
                        }}
                        onClick={() => navigate('/')}
                        onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                    />
                
                </div>

                <div className="login-header">
                    <h1>Welcome Back</h1>
                    <p>Sign in to your account to continue</p>
                </div>

                <Form
                    name="login"
                    initialValues={{ remember: true }}
                    onFinish={onFinish}
                    layout="vertical"
                    className="login-form"
                >
                    <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                            { required: true, message: 'Please input your email!' },
                            { type: 'email', message: 'Please enter a valid email!' }
                        ]}
                    >
                        <Input 
                            prefix={<UserOutlined className="input-icon" />} 
                            placeholder="Enter your email"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: 'Please input your password!' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined className="input-icon" />}
                            placeholder="Enter your password"
                            size="large"
                        />
                    </Form.Item>

                    <div style={{ textAlign: 'right', marginBottom: 16 }}>
                        <Button type="link" onClick={() => navigate('/forgot-password')} style={{ padding: 0 }}>
                            Forgot Password?
                        </Button>
                    </div>

                    <Form.Item className="form-actions">
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            block 
                            size="large"
                            loading={loading}
                        >
                            Sign In
                        </Button>
                    </Form.Item>

                    <div className="form-footer">
                        <p>Don't have an account?</p>
                        <Button 
                            type="link" 
                            onClick={() => navigate('/register')}
                            className="register-link"
                        >
                            Create an account
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default Login; 